import React, { useEffect, useState } from 'react';
import { Button, Image } from 'react-bootstrap';
import {
  ContentState,
  convertFromHTML,
  convertToRaw,
  EditorState,
} from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import '../css/configMessage.css';

import ConfigIcon from '../assets/icon/mensagem.svg';
import api from '../services/apiClient';
import { useToast } from '../hooks/toast';

interface IParametroTermoCondicoes {
  termoCondicoesHtml: string;
}

const fontFamilies = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Verdana',
  'Tahoma',
];

const fontSizes = ['10', '12', '14', '16', '18', '24', '32'];

const createEditorState = (html: string): EditorState => {
  if (!html || html.trim() === '') {
    return EditorState.createEmpty();
  }

  const contentBlock = convertFromHTML(html);
  const contentState = ContentState.createFromBlockArray(
    contentBlock.contentBlocks
  );

  return EditorState.moveFocusToEnd(
    EditorState.createWithContent(contentState)
  );
};

const ConfigTerms: React.FC = () => {
  const { addToast } = useToast();
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [loading, setLoading] = useState(false);

  const loadParametro = (): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get('/parametro', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const result = response.data;

        if (result.success) {
          setEditorState(
            createEditorState(result.data?.termoCondicoesHtml ?? '')
          );
        }
      })
      .catch((error) => {
        addToast({
          title: 'Erro ao carregar termo e condições',
          description: error.message,
          type: 'error',
        });
      });
  };

  useEffect(() => {
    loadParametro();
  }, []);

  const handleSave = (): void => {
    const token = localStorage.getItem('@LiveChat:token');
    const contentState = editorState.getCurrentContent();
    const termoCondicoesHtml = contentState.hasText()
      ? draftToHtml(convertToRaw(contentState))
      : '';
    const data: IParametroTermoCondicoes = {
      termoCondicoesHtml,
    };

    setLoading(true);

    api
      .patch('/parametro/TermoCondicoes', data, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const result = response.data;

        if (result.success) {
          addToast({
            title: 'Dados atualizados com sucesso',
            type: 'success',
          });
          setEditorState(createEditorState(result.data.termoCondicoesHtml));
          return;
        }

        addToast({
          title: result.message,
          type: 'error',
        });
      })
      .catch((error) => {
        addToast({
          title: 'Erro ao gravar termo e condições',
          description: error.message,
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClear = (): void => {
    setEditorState(EditorState.createEmpty());
  };

  return (
    <div className="d-flex flex-column" style={{ height: '100%' }}>
      <div
        className="w-100 d-flex align-items-center ps-3"
        style={{
          backgroundColor: '#1A1A1A',
        }}
      >
        <Image
          src={ConfigIcon}
          style={{ width: '1.7rem', height: '1.7rem' }}
          alt="Termo e Condições"
        />
        <span
          className="text-white ms-2"
          style={{
            fontSize: '1.68rem',
            fontWeight: '600',
            backgroundColor: '#1A1A1A',
          }}
        >
          Termo e Condições
        </span>
      </div>

      <div
        className="d-flex flex-column ms-3 mt-3 me-4 p-3 overflow-hidden"
        style={{
          borderRadius: '0.68rem',
          boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
          flex: 1,
        }}
      >
        <span style={{ fontSize: '1.05rem', color: '#818181' }}>
          O texto cadastrado aqui será exibido na entrada do live-chat-client.
        </span>

        <div
          className="d-flex flex-column mt-3 overflow-auto"
          style={{ flex: 1 }}
        >
          <Editor
            editorState={editorState}
            onEditorStateChange={setEditorState}
            editorClassName="form-control overflow-auto cls-editor-mensagem-padrao"
            wrapperClassName="cls-terms-editor"
            toolbarClassName="cls-terms-toolbar"
            placeholder="Digite o termo e condições"
            localization={{
              locale: 'pt',
              translations: {
                'generic.add': 'Adicionar',
                'generic.cancel': 'Cancelar',
                'components.controls.link.linkTitle': 'Título do link',
                'components.controls.link.linkTarget': 'URL do link',
                'components.controls.link.linkTargetOption':
                  'Abrir em nova janela',
                'components.controls.link.link': 'Inserir link',
                'components.controls.link.unlink': 'Remover link',
              },
            }}
            toolbar={{
              options: [
                'fontFamily',
                'fontSize',
                'inline',
                'list',
                'link',
                'history',
              ],
              fontFamily: {
                options: fontFamilies,
              },
              fontSize: {
                options: fontSizes,
              },
              inline: {
                options: ['bold', 'italic', 'underline'],
              },
            }}
          />
        </div>

        <div className="d-flex justify-content-center align-items-center mt-3">
          <Button
            variant="secondary"
            type="button"
            className="me-1"
            style={{ fontSize: '1.25rem', fontWeight: '600' }}
            onClick={handleClear}
            disabled={loading}
          >
            limpar dados
          </Button>
          <Button
            variant="primary"
            type="button"
            className="ms-1"
            style={{ fontSize: '1.25rem', fontWeight: '600' }}
            onClick={handleSave}
            disabled={loading}
          >
            confirmar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfigTerms;
